// PelicanAppScreen · digitised, animated mock of the Social Trading
// (Trader's Room) UI. Renders inside any phone bezel as its absolute
// inset-0 child. Two variants:
//   "performance" — return-history dashboard with drawing equity curve +
//                   counting return % + live trade ticker
//   "profile"     — trader profile card with rotating markets pie + animated
//                   win-rate ring + COPY button pulse
//
// Both are pure CSS / SVG / framer-motion · no images, no canvas, lightweight.
//
// Theme: the entire screen is theme-aware. We set a small palette of
// CSS custom properties (--pa-*) on the outermost container based on
// the active theme; every inline style + nested component reads from
// those vars. Tailwind text-white utilities are overridden inside the
// `.pa-light-mode` scope via /index.css.
import { animate, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  Activity, BarChart3, Heart, PieChart as PieIcon, Users,
  ArrowUpRight, ArrowDownRight, TrendingUp, Copy, Bell,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/contexts/ThemeContext";

// Palette · all the cosmetic colours that need to swap with theme.
// Dark mode preserves the original look (so existing tests don't break);
// light mode mirrors the same surfaces in a paper-light palette.
function usePalette() {
  const { theme } = useTheme();
  const isLight = theme === "light";
  return isLight
    ? {
        isLight: true,
        bgGrad:    "linear-gradient(180deg, #f7f9fc 0%, #eef2f6 100%)",
        bgDeep:    "#f4f6fa",
        cardBg:    "rgba(10,24,56,0.04)",
        cardBg2:   "rgba(10,24,56,0.03)",
        cardBg3:   "rgba(10,24,56,0.025)",
        cardBgHi:  "rgba(10,24,56,0.06)",
        borderHi:  "rgba(10,24,56,0.10)",
        borderMid: "rgba(10,24,56,0.08)",
        borderLo:  "rgba(10,24,56,0.06)",
        borderSk:  "rgba(10,24,56,0.05)",
        wireRing:  "rgba(10,24,56,0.12)",
      }
    : {
        isLight: false,
        bgGrad:    "linear-gradient(180deg, #0c1620 0%, #060b12 100%)",
        bgDeep:    "#0c1620",
        cardBg:    "rgba(255,255,255,0.04)",
        cardBg2:   "rgba(255,255,255,0.03)",
        cardBg3:   "rgba(255,255,255,0.025)",
        cardBgHi:  "rgba(255,255,255,0.06)",
        borderHi:  "rgba(255,255,255,0.10)",
        borderMid: "rgba(255,255,255,0.08)",
        borderLo:  "rgba(255,255,255,0.06)",
        borderSk:  "rgba(255,255,255,0.05)",
        wireRing:  "rgba(255,255,255,0.08)",
      };
}

/* ============================================================
   Hook · counts a number from `from` → `to` over `duration` s
   ============================================================ */
function useCountUp(to, duration = 2.4, from = 0) {
  const [v, setV] = useState(from);
  useEffect(() => {
    const c = animate(from, to, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (val) => setV(val),
    });
    return () => c.stop();
  }, [to, duration, from]);
  return v;
}

/* ============================================================
   Notch + iOS-style status bar (used by all variants)
   ============================================================ */
function StatusChrome() {
  const P = usePalette();
  // Status text + signal bars switch to dark ink on a light screen so
  // the row stays legible in both themes.
  const statusInk = P.isLight ? "rgba(10,24,56,0.65)" : "rgba(255,255,255,0.70)";
  const barInk    = P.isLight ? "rgba(10,24,56,0.65)" : "rgba(255,255,255,0.70)";
  const batRing   = P.isLight ? "rgba(10,24,56,0.50)" : "rgba(255,255,255,0.60)";
  return (
    <>
      {/* Camera notch · sits inside the phone bezel border. Pushed
          down a touch so it clears the rounded-[36px] bezel mask. */}
      <div
        className="absolute left-1/2 -translate-x-1/2 z-30 rounded-full"
        style={{ top: 10, width: 60, height: 14, background: "#000" }}
      />
      {/* Status text · originally at top:[6px] which sits INSIDE the
          inner radius of the bezel mask — the very top of "9:41" + the
          signal-bar icons were being clipped by the rounded corner.
          top:[16px] + left/right-5 keeps the text comfortably below
          the corner curve so the whole status row renders cleanly. */}
      <div className="absolute top-[16px] left-5 right-5 flex items-center justify-between text-[7.5px] font-mono z-30 pointer-events-none" style={{ color: statusInk }}>
        <span>9:41</span>
        <span className="flex items-center gap-[3px]">
          <span className="inline-block w-0.5 h-1 rounded-sm" style={{ background: barInk }} />
          <span className="inline-block w-0.5 h-1.5 rounded-sm" style={{ background: barInk }} />
          <span className="inline-block w-0.5 h-2 rounded-sm" style={{ background: barInk }} />
          <span className="inline-block w-0.5 h-2.5 rounded-sm" style={{ background: barInk }} />
          <span className="ml-1">5G</span>
          <span className="ml-1.5 inline-block w-3 h-1.5 rounded-[2px] relative" style={{ border: `1px solid ${batRing}` }}>
            <span className="absolute inset-[1px] right-[3px] rounded-[1px]" style={{ background: barInk }} />
          </span>
        </span>
      </div>
    </>
  );
}

/* ============================================================
   Bottom tab bar
   ============================================================ */
function BottomTabBar({ active = "discover", accent = "#46a4ff" }) {
  const P = usePalette();
  const tabs = [
    { id: "discover", Icon: BarChart3 },
    { id: "feed",     Icon: Activity },
    { id: "portfolio", Icon: PieIcon },
    { id: "favorites", Icon: Heart },
    { id: "account",   Icon: Users },
  ];
  // No mt-auto / no absolute positioning · this is a normal flex child so
  // the screen's flex-col layout naturally reserves space for it.
  // Content above can use flex-1 or mt-auto to push itself right up to
  // (but never under) this bar. mt-2 gives breathing room from the strip
  // above; mb-4 gives clearance from the phone bezel bottom.
  return (
    <div
      className="mx-2.5 mt-2 mb-4 h-9 rounded-2xl flex items-center justify-around shrink-0 z-20"
      style={{
        background: P.cardBgHi,
        border: `1px solid ${P.borderLo}`,
        backdropFilter: "blur(8px)",
      }}
    >
      {tabs.map((t) => (
        <div
          key={t.id}
          className="w-6 h-6 grid place-items-center"
          style={{ color: t.id === active ? accent : (P.isLight ? "rgba(10,24,56,0.42)" : "rgba(255,255,255,0.38)") }}
        >
          <t.Icon size={13} strokeWidth={1.8} />
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   Stat tile · one of the small metric boxes
   ============================================================ */
function StatTile({ label, value, color }) {
  const P = usePalette();
  const labelInk = P.isLight ? "rgba(10,24,56,0.55)" : "rgba(255,255,255,0.45)";
  const valueInk = P.isLight ? "#0a1838"             : "white";
  return (
    <div
      className="rounded-md py-1.5 px-2 min-w-0"
      style={{ background: P.cardBg, border: `1px solid ${P.borderLo}` }}
    >
      <div className="font-mono text-[6.5px] uppercase tracking-wider mb-0.5 leading-none truncate" style={{ color: labelInk }}>{label}</div>
      <div className="font-display text-[12px] font-medium tabular-nums leading-tight truncate" style={{ color: color || valueInk }}>{value}</div>
    </div>
  );
}

/* ============================================================
   Animated equity-curve SVG · path-length draw + last-dot pulse
   ============================================================ */
const EQ_POINTS = [
  [0, 108], [10, 105], [20, 100], [30, 96], [40, 102], [50, 88],
  [60, 78], [70, 82], [80, 70], [90, 60], [100, 64], [110, 48],
  [120, 40], [130, 44], [140, 30], [150, 32], [160, 20], [170, 14], [180, 8],
];

function EquityCurve() {
  const P = usePalette();
  const path = EQ_POINTS.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
  const area = `${path} L180,120 L0,120 Z`;
  const lastX = EQ_POINTS[EQ_POINTS.length - 1][0];
  const lastY = EQ_POINTS[EQ_POINTS.length - 1][1];
  return (
    <svg viewBox="0 0 180 120" preserveAspectRatio="none" className="block w-full h-full">
      <defs>
        <linearGradient id="pa-eq-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#54d97c" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#54d97c" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Horizontal grid lines · subtle */}
      {[24, 48, 72, 96].map((y) => (
        <line key={y} x1="0" x2="180" y1={y} y2={y} stroke={P.borderSk} strokeWidth="0.5" strokeDasharray="2 3" />
      ))}
      <motion.path
        d={area}
        fill="url(#pa-eq-grad)"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.6 }}
      />
      <motion.path
        d={path}
        fill="none"
        stroke="#54d97c"
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2.4, ease: "easeInOut" }}
      />
      {/* Pulsing tip */}
      <motion.circle
        cx={lastX} cy={lastY} r="6" fill="#54d97c" opacity="0.3"
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: [0.4, 1.4], opacity: [0.5, 0] }}
        transition={{ delay: 2.4, duration: 1.6, repeat: Infinity }}
      />
      <motion.circle
        cx={lastX} cy={lastY} r="2.5" fill="#54d97c"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 0.4 }}
      />
    </svg>
  );
}

/* ============================================================
   Performance screen variant
   ============================================================ */
const TRADES = [
  { sym: "EURUSD", pnl: 142.5,   up: true },
  { sym: "XAUUSD", pnl: 420.18,  up: true },
  { sym: "GBPJPY", pnl: -28.3,   up: false },
  { sym: "BTCUSD", pnl: 1205.0,  up: true },
  { sym: "US100",  pnl: 87.4,    up: true },
  { sym: "AUDUSD", pnl: -14.6,   up: false },
  { sym: "USTEC",  pnl: 318.9,   up: true },
];

function PerformanceScreen({ accent = "#46a4ff" }) {
  const { t } = useTranslation();
  const P = usePalette();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TRADES.length), 3000);
    return () => clearInterval(t);
  }, []);
  const tr = TRADES[idx];
  const retPct = useCountUp(9373, 2.6);
  const profit = useCountUp(58432, 2.6);

  const text1 = P.isLight ? "#0a1838"             : "white";
  const text3 = P.isLight ? "rgba(10,24,56,0.55)" : "rgba(255,255,255,0.45)";
  const text4 = P.isLight ? "rgba(10,24,56,0.45)" : "rgba(255,255,255,0.40)";
  const text5 = P.isLight ? "rgba(10,24,56,0.35)" : "rgba(255,255,255,0.30)";

  return (
    <div
      className="absolute inset-0 flex flex-col overflow-hidden"
      style={{ background: P.bgGrad }}
    >
      <StatusChrome />

      {/* App header */}
      <div className="px-3 pt-[32px] pb-1">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[6.5px] uppercase tracking-[0.22em]" style={{ color: accent }}>
              {t("pelican.appName", "Social Trading")}
            </div>
            <div className="font-display text-[12px] font-medium leading-tight" style={{ color: text1 }}>
              {t("pelican.returnHistory", "Return History")}
            </div>
          </div>
          <div className="flex items-center gap-1 text-[7px] font-mono uppercase tracking-wider" style={{ color: text3 }}>
            <motion.span
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.6, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full inline-block"
              style={{ background: "#54d97c" }}
            />
            {t("pelican.live", "LIVE")}
          </div>
        </div>
      </div>

      {/* Period chips */}
      <div className="px-3 flex gap-1 mb-1.5">
        {["1D", "1W", "1M", "3M", "1Y", "ALL"].map((p) => {
          const isActive = p === "ALL";
          return (
            <div
              key={p}
              className="font-mono text-[7px] uppercase px-1.5 py-0.5 rounded leading-none"
              style={{
                background: isActive ? `color-mix(in srgb, ${accent} 22%, transparent)` : "transparent",
                color: isActive ? accent : text4,
                border: `1px solid ${isActive ? `color-mix(in srgb, ${accent} 35%, transparent)` : "transparent"}`,
              }}
            >
              {p}
            </div>
          );
        })}
      </div>

      {/* Big return */}
      <div className="px-3">
        <div className="font-mono text-[6.5px] uppercase tracking-wider leading-none" style={{ color: text4 }}>
          {t("pelican.return12m", "12-month return")}
        </div>
        <div className="font-display text-[22px] font-semibold tabular-nums leading-none mt-0.5" style={{ color: "#54d97c" }}>
          +{Math.round(retPct).toLocaleString()}%
        </div>
        <div className="font-mono text-[8px] mt-0.5" style={{ color: text3 }}>
          ≈ ${Math.round(profit).toLocaleString()} {t("pelican.profit", "profit")}
        </div>
      </div>

      {/* Animated curve */}
      <div className="px-1.5 mt-1.5" style={{ height: 64 }}>
        <EquityCurve />
      </div>

      {/* Stat tiles */}
      <div className="px-2.5 grid grid-cols-3 gap-1.5 mt-1.5">
        <StatTile label={t("comp.art.trades")} value="12,158" />
        <StatTile label={t("comp.art.wins")} value="94.2%" color="#54d97c" />
        <StatTile label={t("comp.art.loss")} value="5.8%" color="#f5605c" />
      </div>

      {/* Recent activity feed · 3-row trade list with the top row animating */}
      <div className="px-2.5 mt-1.5 flex-1 flex flex-col gap-1 min-h-0 overflow-hidden">
        <div className="flex items-center justify-between leading-none">
          <span className="font-mono text-[6.5px] uppercase tracking-[0.18em]" style={{ color: text3 }}>{t("pelican.recentActivity", "Recent activity")}</span>
          <span className="font-mono text-[6.5px] uppercase tracking-wider" style={{ color: text5 }}>{t("pelican.justNow", "just now")}</span>
        </div>
        {[0, 1, 2].map((row) => {
          const tr = TRADES[(idx + row) % TRADES.length];
          const isTop = row === 0;
          const ageLabel = ["12s ago", "47s ago", "2m ago"][row];
          return (
            <motion.div
              key={`row-${row}-${idx}`}
              initial={isTop ? { opacity: 0, y: -8, scale: 0.96 } : { opacity: 0, y: -3 }}
              animate={{ opacity: isTop ? 1 : 0.72, y: 0, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-md px-1.5 py-1 flex items-center justify-between"
              style={{
                background: isTop
                  ? (tr.up ? "rgba(80,220,130,0.12)" : "rgba(245,96,92,0.12)")
                  : P.cardBg2,
                border: `1px solid ${isTop
                  ? (tr.up ? "rgba(80,220,130,0.34)" : "rgba(245,96,92,0.34)")
                  : P.borderSk}`,
              }}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <div
                  className="w-3.5 h-3.5 rounded-[3px] grid place-items-center shrink-0"
                  style={{ background: tr.up ? "#54d97c" : "#f5605c" }}
                >
                  {tr.up
                    ? <ArrowUpRight size={8} strokeWidth={2.6} style={{ color: "#071116" }} />
                    : <ArrowDownRight size={8} strokeWidth={2.6} style={{ color: "#071116" }} />}
                </div>
                <span className="font-mono text-[8px] truncate" style={{ color: text1 }}>{tr.sym}</span>
                <span className="font-mono text-[6.5px] uppercase tracking-wider hidden xs:inline" style={{ color: text5 }}>{tr.up ? t("pelican.buy", "Buy") : t("pelican.sell", "Sell")}</span>
                {isTop && (
                  <motion.span
                    className="font-mono text-[6px] uppercase tracking-wider px-1 rounded-[3px]"
                    style={{ background: "rgba(80,220,130,0.18)", color: "#54d97c" }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.4, repeat: Infinity }}
                  >{t("pelican.new", "NEW")}</motion.span>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!isTop && <span className="font-mono text-[6px] hidden sm:inline" style={{ color: text5 }}>{ageLabel}</span>}
                <span className="font-display text-[12px] font-semibold tabular-nums" style={{ color: tr.up ? "#54d97c" : "#f5605c" }}>
                  {tr.up ? "+" : ""}${Math.abs(tr.pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </motion.div>
          );
        })}

        {/* Mini stat strip · pushes to bottom of the flex-1 area */}
        <div className="grid grid-cols-3 gap-1 mt-auto pt-1">
          <div className="rounded-md px-1.5 py-1 text-center" style={{ background: P.cardBg2, border: `1px solid ${P.borderSk}` }}>
            <div className="font-mono text-[5.5px] uppercase tracking-wider leading-none" style={{ color: text4 }}>{t("pelican.copiers", "Copiers")}</div>
            <div className="font-display text-[12px] font-medium tabular-nums leading-tight" style={{ color: text1 }}>847</div>
          </div>
          <div className="rounded-md px-1.5 py-1 text-center" style={{ background: P.cardBg2, border: `1px solid ${P.borderSk}` }}>
            <div className="font-mono text-[5.5px] uppercase tracking-wider leading-none" style={{ color: text4 }}>{t("pelican.sharpe", "Sharpe")}</div>
            <div className="font-display text-[12px] font-medium tabular-nums leading-tight" style={{ color: text1 }}>2.34</div>
          </div>
          <div className="rounded-md px-1.5 py-1 text-center" style={{ background: "rgba(80,220,130,0.10)", border: "1px solid rgba(80,220,130,0.28)" }}>
            <div className="font-mono text-[5.5px] uppercase tracking-wider leading-none" style={{ color: text3 }}>{t("pelican.streak", "Streak")}</div>
            <div className="font-display text-[12px] font-medium tabular-nums leading-tight" style={{ color: "#54d97c" }}>14W</div>
          </div>
        </div>
      </div>

      <BottomTabBar active="discover" accent={accent} />
    </div>
  );
}

/* ============================================================
   Profile screen variant · rotating markets pie + COPY button
   ============================================================ */
const MARKETS = [
  { name: "Forex",   pct: 42, color: "#46a4ff" },
  { name: "Metals",  pct: 24, color: "#f5b14a" },
  { name: "Indices", pct: 18, color: "#a78bfa" },
  { name: "Crypto",  pct: 11, color: "#54d97c" },
  { name: "Energy",  pct: 5,  color: "#f5605c" },
];

function MarketsPie({ size = 84 }) {
  const P = usePalette();
  const cx = size / 2, cy = size / 2, r = size / 2 - 4;
  let cum = 0;
  const segments = MARKETS.map((m) => {
    const start = (cum / 100) * Math.PI * 2 - Math.PI / 2;
    cum += m.pct;
    const end = (cum / 100) * Math.PI * 2 - Math.PI / 2;
    const large = m.pct > 50 ? 1 : 0;
    const x1 = cx + Math.cos(start) * r;
    const y1 = cy + Math.sin(start) * r;
    const x2 = cx + Math.cos(end) * r;
    const y2 = cy + Math.sin(end) * r;
    return { d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`, color: m.color };
  });
  return (
    <motion.svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      animate={{ rotate: 360 }}
      transition={{ duration: 26, ease: "linear", repeat: Infinity }}
    >
      {segments.map((s, i) => (
        <motion.path
          key={i}
          d={s.d}
          fill={s.color}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 0.9, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.6 + i * 0.07 }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
        />
      ))}
      {/* Inner hole · donut · theme-matched bg colour */}
      <circle cx={cx} cy={cy} r={r * 0.55} fill={P.bgDeep} />
    </motion.svg>
  );
}

function WinRateRing({ value = 94 }) {
  const P = usePalette();
  const ink = P.isLight ? "#0a1838" : "white";
  const C = 2 * Math.PI * 22; // circumference of r=22
  const offset = C * (1 - value / 100);
  return (
    <div className="relative w-[60px] h-[60px]">
      <svg viewBox="0 0 60 60" className="absolute inset-0 -rotate-90">
        <circle cx="30" cy="30" r="22" fill="none" stroke={P.wireRing} strokeWidth="5" />
        <motion.circle
          cx="30" cy="30" r="22"
          fill="none"
          stroke="#54d97c"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.6, ease: "easeOut", delay: 0.4 }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-display text-[12px] font-semibold tabular-nums" style={{ color: ink }}>
        {value}%
      </div>
    </div>
  );
}

function ProfileScreen({ accent = "#46a4ff" }) {
  const { t } = useTranslation();
  const P = usePalette();
  const followers = useCountUp(2148, 2.2);
  const text1 = P.isLight ? "#0a1838"             : "white";
  const text3 = P.isLight ? "rgba(10,24,56,0.55)" : "rgba(255,255,255,0.45)";
  const text4 = P.isLight ? "rgba(10,24,56,0.45)" : "rgba(255,255,255,0.40)";
  const text5 = P.isLight ? "rgba(10,24,56,0.35)" : "rgba(255,255,255,0.30)";

  return (
    <div
      className="absolute inset-0 flex flex-col overflow-hidden"
      style={{ background: P.bgGrad }}
    >
      <StatusChrome />

      {/* Header */}
      <div className="px-3 pt-[32px] pb-1 flex items-center justify-between">
        <div>
          <div className="font-mono text-[6.5px] uppercase tracking-[0.22em]" style={{ color: accent }}>{t("pelican.appName", "Social Trading")}</div>
          <div className="font-display text-[12px] font-medium leading-tight" style={{ color: text1 }}>{t("pelican.discoverTraders", "Discover Traders")}</div>
        </div>
        <div className="w-5 h-5 grid place-items-center rounded-full" style={{ background: P.cardBgHi }}>
          <Bell size={10} strokeWidth={1.8} style={{ color: text3 }} />
        </div>
      </div>

      {/* Avatar + identity */}
      <div className="px-3 mt-1.5 flex items-center gap-2.5">
        <motion.div
          className="w-12 h-12 rounded-full grid place-items-center font-display text-[18px] font-semibold relative"
          style={{ background: `conic-gradient(from 0deg, ${accent}, #54d97c, ${accent})`, color: "#071116" }}
          animate={{ scale: [1, 1.04, 1] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="absolute inset-[2px] rounded-full grid place-items-center" style={{ background: P.bgDeep }}>
            <span style={{ color: accent }}>H</span>
          </div>
        </motion.div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[12px] font-medium leading-tight truncate" style={{ color: text1 }}>{t("pelican.holyGrail", "The Holy Grail")}</div>
          <div className="flex items-center gap-1 font-mono text-[7px] uppercase tracking-wider mt-px" style={{ color: text3 }}>
            <span className="tabular-nums">{Math.round(followers).toLocaleString()}</span>
            <span>{t("pelican.followers", "followers")}</span>
            <span style={{ color: text5 }}>·</span>
            <span style={{ color: "#54d97c" }}>{t("pelican.verified", "VERIFIED")}</span>
          </div>
        </div>
      </div>

      {/* Tab pill row */}
      <div className="px-3 mt-2 flex gap-1">
        {[t("pelican.tab.about", "About"), t("pelican.tab.performance", "Performance"), t("pelican.tab.trades", "Trades"), t("pelican.tab.markets", "Markets")].map((label, i) => (
          <div
            key={label}
            className="font-mono text-[7px] uppercase px-1.5 py-0.5 rounded leading-none flex-1 text-center"
            style={{
              background: i === 1 ? `color-mix(in srgb, ${accent} 22%, transparent)` : "transparent",
              color: i === 1 ? accent : text4,
              border: `1px solid ${i === 1 ? `color-mix(in srgb, ${accent} 35%, transparent)` : P.borderLo}`,
            }}
          >
            {label}
          </div>
        ))}
      </div>

      {/* Pie + win rate */}
      <div className="px-3 mt-1.5 flex items-center gap-2.5">
        <div className="relative shrink-0">
          <MarketsPie size={64} />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="font-mono text-[5.5px] uppercase tracking-wider leading-none" style={{ color: text4 }}>{t("comp.art.tabMarkets")}</div>
            <div className="font-display text-[12px] font-semibold leading-tight" style={{ color: text1 }}>5</div>
          </div>
        </div>
        <div className="flex-1 min-w-0 grid grid-cols-2 gap-1.5">
          <StatTile label={t("pelican.return12mShort", "Return 12m")} value="+9,373%" color="#54d97c" />
          <StatTile label={t("comp.art.winRate")} value="94.16%" color="#54d97c" />
          <StatTile label={t("comp.art.leverageLabel")} value="1:500" />
          <StatTile label={t("comp.art.avgTrade")} value="2h 14m" />
        </div>
      </div>

      {/* 7-day sparkline · single compact row */}
      <div className="px-3 mt-1.5 rounded-md py-1 px-2 mx-3"
        style={{ background: P.cardBg, border: `1px solid ${P.borderLo}` }}>
        <div className="flex items-center justify-between leading-none mb-0.5">
          <span className="font-mono text-[5.5px] uppercase tracking-wider" style={{ color: text3 }}>{t("pelican.last7Days", "Last 7 days")}</span>
          <span className="font-display text-[8.5px] font-semibold tabular-nums" style={{ color: "#54d97c" }}>+24.6%</span>
        </div>
        <svg viewBox="0 0 180 18" className="block w-full h-[14px]" preserveAspectRatio="none">
          <defs>
            <linearGradient id="pa-spark-grad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#54d97c" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#54d97c" stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path
            d="M0,14 L25,12 L50,13 L75,8 L100,9 L125,5 L150,3 L180,2"
            fill="none"
            stroke="#54d97c"
            strokeWidth="1.4"
            strokeLinejoin="round"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.6, ease: "easeOut", delay: 0.8 }}
          />
          <motion.path
            d="M0,14 L25,12 L50,13 L75,8 L100,9 L125,5 L150,3 L180,2 L180,18 L0,18 Z"
            fill="url(#pa-spark-grad)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.6 }}
          />
        </svg>
      </div>

      {/* Recent trades · 3-row mini list to fill the 19.5:9 phone */}
      <div className="px-3 mt-2">
        <div className="flex items-center justify-between leading-none mb-1">
          <span className="font-mono text-[6.5px] uppercase tracking-[0.18em]" style={{ color: text3 }}>{t("pelican.recentTrades", "Recent trades")}</span>
          <span className="font-mono text-[6.5px] uppercase tracking-wider" style={{ color: accent }}>{t("pelican.viewAll", "View all")} ›</span>
        </div>
        <div className="space-y-0.5">
          {[
            { sym: "XAUUSD", pnl: 420.18, up: true,  ago: "12s" },
            { sym: "EURUSD", pnl: 142.50, up: true,  ago: "47s" },
            { sym: "BTCUSD", pnl: 1205.0, up: true,  ago: "2m" },
          ].map((tr) => (
            <div key={tr.sym} className="flex items-center justify-between rounded px-1.5 py-1"
              style={{ background: P.cardBg3 }}>
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="w-3 h-3 rounded-[2px] grid place-items-center shrink-0"
                  style={{ background: tr.up ? "#54d97c" : "#f5605c" }}>
                  {tr.up
                    ? <ArrowUpRight size={7} strokeWidth={2.6} style={{ color: "#071116" }} />
                    : <ArrowDownRight size={7} strokeWidth={2.6} style={{ color: "#071116" }} />}
                </div>
                <span className="font-mono text-[7.5px]" style={{ color: text1 }}>{tr.sym}</span>
                <span className="font-mono text-[6.5px]" style={{ color: text5 }}>{tr.ago}</span>
              </div>
              <span className="font-display text-[8.5px] font-semibold tabular-nums"
                style={{ color: tr.up ? "#54d97c" : "#f5605c" }}>
                {tr.up ? "+" : ""}${Math.abs(tr.pnl).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* COPY button · pinned to the bottom of the content area */}
      <div className="px-3 mt-auto">
        <motion.div
          className="rounded-xl py-2 flex items-center justify-center gap-1.5 font-display text-[12px] font-semibold tracking-wide"
          style={{ background: accent, color: "#071116" }}
          animate={{ boxShadow: [`0 0 0 0 color-mix(in srgb, ${accent} 50%, transparent)`, `0 0 0 8px color-mix(in srgb, ${accent} 0%, transparent)`] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        >
          <Copy size={12} strokeWidth={2.2} /> {t("pelican.copyTrader", "COPY TRADER")}
        </motion.div>
      </div>

      <BottomTabBar active="discover" accent={accent} />
    </div>
  );
}

/* ============================================================
   Dispatcher (default export)
   ============================================================ */
export default function PelicanAppScreen({ variant = "performance", accent }) {
  if (variant === "profile") return <ProfileScreen accent={accent} />;
  return <PerformanceScreen accent={accent} />;
}
